import { useTranslation } from "react-i18next";
import { ButtonSkeleton } from "~/common/components/CommonSkeletons";
import { C_OUTLINE_VARIANT } from "~/common/components/icon/Colors";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import {
  Skeleton,
  Stack,
  Card,
  styled,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableRow,
  TableCell,
} from "~/common/mui";
// @ts-ignore
import { reset } from "sync-scroll";
import { useEffect } from "react";
import { ComparisonOverlayContent } from "./ComparisonOverlayStyles";

export const ProductComparisonSkeleton = () => {
  const { t } = useTranslation();
  const isDesktop = useIsMinDesktopSize();

  useEffect(() => {
    const tables = document.querySelectorAll("table");

    tables.forEach((table) => {
      table.parentElement?.parentElement?.classList.add("syncscroll");
      table.parentElement?.parentElement?.setAttribute("name", "myElements");
    });

    reset();
  }, []);

  useEffect(() => {
    reset();
  }, []);

  const singleCellSkeleton = (key: number, width: number = 100) => (
    <TableCell
      key={key}
      sx={{
        width: key == 0 ? 246 : 250,
        minWidth: key == 0 ? 246 : 250,
        maxWidth: key == 0 ? 246 : 250,
        borderLeft: key == 0 ? 0 : `1px solid ${C_OUTLINE_VARIANT}`,
      }}
    >
      <Skeleton width={width} height={20} />
    </TableCell>
  );

  const numberOfColumns = isDesktop ? 5 : 4;
  return (
    <ComparisonOverlayContent>
      <Stack gap={4}>
        {/* Sticky Header */}
        <Stack
          gap={2}
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            pt: 2,
            backgroundColor: "white",
          }}
        >
          <Stack direction={isDesktop ? "row" : "column"} gap={3}>
            {/* Sidebar */}
            <ComparisonSidebarContent sx={{ minWidth: 222 }}>
              <Skeleton variant="rounded" width={"222px"} height={40} />

              {isDesktop && (
                <Stack gap={2} direction={"row"}>
                  <Skeleton variant="rounded" width={52} height={32} />
                  <Skeleton variant="rounded" width={97} height={20} />
                </Stack>
              )}
            </ComparisonSidebarContent>

            <Stack gap={2} sx={{ minWidth: 0, flex: 1 }}>
              {/* @ts-ignore */}
              <ScrollableCardsList className="syncscroll" name="myElements">
                {Array.from({ length: 4 }).map((_, index) => (
                  <Card
                    key={index}
                    sx={{
                      width: 242,
                      minWidth: 242,
                      p: 2,
                    }}
                  >
                    <Stack gap={2}>
                      <Stack gap={1}>
                        <Stack direction={"row"} justifyContent={"space-between"}>
                          <Skeleton variant="rounded" height={64} width={64} />
                          <ButtonSkeleton width={24} height={24} />
                        </Stack>
                        <Typography variant="titleLarge">
                          <Skeleton width={146} />
                        </Typography>
                      </Stack>
                      <Typography variant="titleLarge">
                        <Skeleton width={81} />
                      </Typography>
                      <Stack direction={"row"} gap={1}>
                        <ButtonSkeleton width={40} height={40} />
                        <ButtonSkeleton width={40} height={40} />
                      </Stack>
                    </Stack>
                  </Card>
                ))}
              </ScrollableCardsList>
            </Stack>
          </Stack>
        </Stack>
        <Stack>
          <Accordion defaultExpanded>
            <AccordionSummary>
              <Typography component="b">{t("productInfoTable.tableName")}</Typography>
            </AccordionSummary>
            {/* @ts-ignore */}
            <AccordionDetails sx={{ pl: 0, overflowX: "auto" }} className="syncscroll" name="myElements">
              <Table sx={{ tableLayout: "fixed" }}>
                <TableBody>
                  {[...Array(4)].map((e, i) => (
                    <TableRow key={i}>{[...Array(numberOfColumns)].map((e, i) => singleCellSkeleton(i, 100))}</TableRow>
                  ))}
                </TableBody>
              </Table>
            </AccordionDetails>
          </Accordion>
          <Accordion defaultExpanded>
            <AccordionSummary>
              <Typography component="b">{t("commercialTable.tableName")}</Typography>
            </AccordionSummary>
            {/* @ts-ignore */}
            <AccordionDetails sx={{ pl: 0, overflowX: "auto" }} className="syncscroll" name="myElements">
              <Table sx={{ tableLayout: "fixed" }}>
                <TableBody>
                  {[...Array(6)].map((e, i) => (
                    <TableRow key={i}>{[...Array(numberOfColumns)].map((e, i) => singleCellSkeleton(i, 100))}</TableRow>
                  ))}
                </TableBody>
              </Table>
            </AccordionDetails>
          </Accordion>
        </Stack>
      </Stack>
    </ComparisonOverlayContent>
  );
};

const ComparisonSidebarContent = styled(Stack)(({ theme }) => ({
  position: "sticky",
  left: 0,
  zIndex: 9999,
  backgroundColor: theme.palette.common.white,
  gap: "16px",
}));

const ScrollableCardsList = styled("div")(({ theme: _theme }) => ({
  display: "flex",
  gap: "8px",
  overflowX: "auto",
  overflowY: "hidden",
  scrollbarWidth: "none",
  msOverflowStyle: "none",
  "&::-webkit-scrollbar": {
    display: "none",
  },
}));
